import { list } from "postcss";
import { commonConstants } from "../actions/constants/commonConstants";
import { request, success, failure } from "@/utils/constants";
import { type PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";

export const getServiceListReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getServicesListRequest:
            return {
                ...request,
            };
        case commonConstants.getServicesListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getServicesListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const setLoading = (
    state = {},
    action: PayloadAction<{ loading: boolean; message?: string } | boolean>,
) => {
    switch (action.type) {
        case commonConstants.setLoading: {
            if (typeof action.payload === "boolean") {
                return {
                    isLoading: action.payload,
                };
            } else {
                return {
                    isLoading: action.payload.loading,
                    message: action.payload.message,
                };
            }
        }
        default:
            return state;
    }
};

export const getSpecializationsListReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getSpecializationsListRequest:
            return {
                ...request,
            };
        case commonConstants.getSpecializationsListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getSpecializationsListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const getDocumentTypesListReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getDocumentTypesListRequest:
            return {
                ...request,
            };
        case commonConstants.getDocumentTypesListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getDocumentTypesListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const uploadFileReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.uploadFileRequest:
            return {
                ...request,
                uploadedFile: null,
            };
        case commonConstants.uploadFileSuccess:
            return {
                ...success,
                uploadedFile: action.payload,
            };
        case commonConstants.uploadFileFailure:
            return {
                ...failure,
                uploadedFile: null,
                error: action.payload,
            };
        default:
            return state;
    }
};

export const removeFileReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.removeFileRequest:
            return {
                ...request,
            };
        case commonConstants.removeFileSuccess:
            return {
                ...success,
            };
        case commonConstants.removeFileFailure:
            return {
                ...failure,
                error: action.payload,
            };
        default:
            return state;
    }
};
interface NotificationState {
    isSuccess?: boolean;
    isError?: boolean;
    message?: string;
}

const initialState: NotificationState = {};

export const notificationReducer = (
    state: NotificationState = initialState,
    action: any,
): NotificationState => {
    switch (action.type) {
        case commonConstants.showSuccessMessage:
            return {
                isSuccess: true,
                isError: false,
                message: action.payload,
            };
        case commonConstants.showErrorMessage:
            return {
                isSuccess: false,
                isError: true,
                message: action.payload,
            };
        default:
            return state;
    }
};

export const getRoleReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getRoleRequest:
            return {
                ...request,
            };
        case commonConstants.getRoleSuccess:
            return {
                ...success,
                roles: action.payload,
            };
        case commonConstants.getRoleFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const getStudioListReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getStudioListRequest:
            return {
                ...request,
            };
        case commonConstants.getStudioListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getStudioListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const getAlbumTemplatesReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getAlbumTemplatesRequest:
            return { ...request };
        case commonConstants.getAlbumTemplatesSuccess:
            return { ...success, list: action.payload };
        case commonConstants.getAlbumTemplatesFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const getNotificationCount = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getNotificationCountRequest:
            return {
                ...request,
            };
        case commonConstants.getNotificationCountSuccess:
            return {
                ...success,
                count: action.payload,
            };
        case commonConstants.getNotificationCountFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const listNotificationsReducer = (
    state: any = { data: [], total: 0, page: 1, totalPages: 1, isLoadingMore: false },
    action: any,
) => {
    switch (action.type) {
        case commonConstants.listNotificationsRequest:
            return action.input?.payload?.append
                ? { ...state, isLoadingMore: true }
                : { ...state, ...request, isLoadingMore: false };
        case commonConstants.listNotificationsSuccess: {
            const shouldAppend = state.isLoadingMore;

            return {
                ...success,
                data: shouldAppend
                    ? [...(state.data || []), ...action.payload.data]
                    : action.payload.data,
                total: action.payload.total,
                page: action.payload.page,
                totalPages: action.payload.totalPages,
                isLoadingMore: false,
            };
        }
        case commonConstants.listNotificationsFailure:
            return { ...state, ...failure, isLoadingMore: false };
        case commonConstants.markAllReadRequest:
        case commonConstants.markAllReadSuccess:
            return {
                ...state,
                data: (state.data || []).map((notification: any) => ({
                    ...notification,
                    isRead: true,
                })),
            };
        // Optimistically flip isRead on a single item when marked read
        case commonConstants.markOneReadRequest:
            return {
                ...state,
                data: (state.data || []).map((n: any) =>
                    String(n._id) === String(action.payload?.id) ? { ...n, isRead: true } : n,
                ),
            };
        default:
            return state;
    }
};

export const markAllReadReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.markAllReadRequest:
            return { ...request };
        case commonConstants.markAllReadSuccess:
            return { ...success };
        case commonConstants.markAllReadFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const markOneReadReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.markOneReadRequest:
            return { ...request };
        case commonConstants.markOneReadSuccess:
            return { ...success };
        case commonConstants.markOneReadFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const fetchNotificationSettingsReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.fetchNotificationSettingsRequest:
            return { ...request };

        case commonConstants.fetchNotificationSettingsSuccess:
            return { ...success, settings: action.payload };
        case commonConstants.fetchNotificationSettingsFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const updateNotificationSettingsReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.updateNotificationSettingsRequest:
            return { ...request };
        case commonConstants.updateNotificationSettingsSuccess:
            return { ...success, settings: action.payload };
        case commonConstants.updateNotificationSettingsFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const contactUsReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.contactUsRequest:
            return {
                ...request,
            };

        case commonConstants.contactUsSuccess:
            return {
                ...success,
                data: action.payload,
            };

        case commonConstants.contactUsFailure:
            return {
                ...failure,
                error: action.payload,
            };
    }
};

export const getRoutes = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getRoutesListRequest:
            return {
                ...request,
            };
        case commonConstants.getRoutesListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getRoutesListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const accessibleRoutes = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.getAccessibleRoutesListRequest:
            return {
                ...request,
            };
        case commonConstants.getAccessibleRoutesListSuccess:
            return {
                ...success,
                list: action.payload,
            };
        case commonConstants.getAccessibleRoutesListFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};
export const viewNotificationReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.viewNotificationRequest:
            return { ...request };
        case commonConstants.viewNotificationSuccess:
            return { ...success, data: action.payload };
        case commonConstants.viewNotificationFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const kycNameCheckReducer = (state = {}, action: any) => {
    switch (action.type) {
        case commonConstants.kycNameCheckRequest:
            return { ...request };
        case commonConstants.kycNameCheckSuccess:
            return { ...success, data: action.payload };
        case commonConstants.kycNameCheckFailure:
            return { ...failure, data: action.payload };
        default:
            return state;
    }
};
export interface generateSessionSasProps {
    isLoading: boolean;
    isSuccess: boolean;
    isFailure: boolean;
    data?: any;
}
export const generateSessionSasReducer = (
    state: generateSessionSasProps | {} = {},
    action: any,
): generateSessionSasProps | {} => {
    switch (action.type) {
        case commonConstants.generateSessionSasRequest:
            return { ...request };
        case commonConstants.generateSessionSasSuccess:
            return { ...success, data: action.payload };
        case commonConstants.generateSessionSasFailure:
            return { ...failure, data: action.payload };
        default:
            return state;
    }
};
